import { useState, useEffect, useRef, useMemo } from "react";
import { useTheme } from "next-themes";
import Modal from "bootstrap/js/dist/modal";
import {
  categoryButton,
  customModalCancelButton,
  customPortfolioModalButton,
  tabButton,
  themeBasedGoalFormControl,
} from "../components/customStyles";
import { getUnitholderStatement } from "../services/portfolio.service";
import { BankIcon } from "../components/icons";
import dayjs from "dayjs";
import Transactions from "../components/transactions";
import { useTranslation } from "react-i18next";
import FundSelector from "../components/fundSelector";
import useWindowSize from "../hooks/useWindowSize";
import { useAuth } from "../store/AuthContext";
import toast from "react-hot-toast";
import { customToast } from "../components/toasts";
import { useSearchParams } from "react-router-dom";

const allOption = (t) => {
  return {
    value: "*",
    label: t("portfolio.all"),
    isFixed: true,
  };
};

function groupByCategory(items = [], opts = { normalize: true }) {
  const { normalize } = opts;
  const groups = new Map();

  for (const item of items) {
    let key = item?.nature;
    if (normalize) {
      key = typeof key === "string" ? key.trim() : key;
    }
    const name = key && key !== "" ? key : "Unspecified";

    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(item);
  }

  // return as array preserving order of first appearance
  return Array.from(groups.entries()).map(([name, items]) => ({
    name,
    items,
    count: items.length,
  }));
}

const parseDate = (item) => {
  if (item.settlement_time != null) {
    return dayjs(item.settlement_time);
  }
  return dayjs(item.settlement_date);
};

export default function InvestorStatement({ funds, portfolioFunds, setActiveSubMenu }) {
  const { t } = useTranslation();
  const [statementDateRange, setStatementDateRange] = useState(() => ({
    from: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    to: dayjs().format("YYYY-MM-DD"),
    days: 30,
    category: { name: "All" },
    fund: null,
    search: "",
    date: "month",
  }));
  const [updatedStatement, setUpdatedStatement] = useState(() => ({
    funds: [allOption(t)],
    from: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    to: dayjs().format("YYYY-MM-DD"),
    days: 30,
    category: { name: "All" },
    fund: null,
    search: "",
    date: "Month",
  }));
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const { theme } = useTheme();
  const screenSize = useWindowSize();
  const { user } = useAuth();

  const [searchParams] = useSearchParams();
  // const searchTerm = searchParams.get("search");
  // const searchQuery = searchParams.get("query");
  const purchaseSearchModal = searchParams.get("modal");
  const purchaseSearchSymbol = searchParams.get("symbol");

  const statementModalRef = useRef(null);
  const statementModalInstanceRef = useRef(null);

  const searchSelectFunds = useMemo(() => {
    const category = statementDateRange?.category?.name ?? "All";
    const searchTerm = (statementDateRange?.search ?? "").trim().toLowerCase();

    let base = Array.isArray(portfolioFunds) ? portfolioFunds : [];

    if (category !== "All") {
      base = base.filter((item) => (item?.nature ?? "") === category);
    }

    if (!searchTerm) return base;

    const matches = (item) => {
      const symbol = String(item?.symbol_code ?? "").toLowerCase();
      const name = String(item?.fund_name ?? "").toLowerCase();
      return symbol.includes(searchTerm) || name.includes(searchTerm);
    };

    return base.filter(matches);
  }, [statementDateRange.search, statementDateRange.category, portfolioFunds]);

  const fundCategories = groupByCategory(portfolioFunds || []);

  const handleRangePreset = (preset) => {
    setStatementDateRange((prev) => {
      // const toDate = prev.to ? dayjs(prev.to) : dayjs();
      const toDate = dayjs();
      let fromDate;

      switch (preset) {
        case "week":
          fromDate = toDate.subtract(1, "week");
          break;
        case "month":
          fromDate = toDate.subtract(1, "month");
          break;
        case "quarter":
          fromDate = toDate.subtract(3, "month");
          break;
        case "half":
          fromDate = toDate.subtract(6, "month");
          break;
        case "year":
          fromDate = toDate.subtract(1, "year");
          break;
        case "date":
        default:
          // 'date' or unknown: don't change from/to/days here
          return prev;
      }

      const from = fromDate.format("YYYY-MM-DD");
      const to = toDate.format("YYYY-MM-DD");
      const days = toDate.diff(fromDate, "day"); // difference in days (exclusive of +1)
      return { ...prev, date: preset, from, to, days };
    });
  };

  const handleFilterStatement = () => {
    setUpdatedStatement({
      fund: statementDateRange.fund?.symbol_code,
      from: statementDateRange.from,
      to: statementDateRange.to,
      days: statementDateRange.days,
    });
  };

  const handleStatementDateChange = (e) => {
    const { name, value } = e.target;

    setStatementDateRange((prev) => {
      const days = prev.days ?? 0;

      if (name === "date-from") {
        const from = value;
        const to = dayjs(from).add(days, "day").format("YYYY-MM-DD");
        return { ...prev, from, to };
      }

      if (name === "date-to") {
        const to = value;
        const from = dayjs(to).subtract(days, "day").format("YYYY-MM-DD");
        return { ...prev, to, from };
      }

      return prev;
    });
  };

  const handleDaysChange = (e) => {
    const newDiff = Number(e.target.value);
    if (isNaN(newDiff) || newDiff < 0) {
      // optionally you could clamp or ignore invalid entries
      setStatementDateRange((prev) => ({ ...prev, days: 0 }));
      return;
    }
    if (newDiff > 370) {
      toast((t) => customToast("Statement can only be requested for up to 370 days. Please pick a shorter range.", "error", t), {
        duration: 300000,
        removeDelay: 1000,
        style: {
          padding: "0.75rem",
          margin: "0",
          width: "380px",
          maxWidth: "100%",
          // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
          // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
          color: "rgb(33,37,41)",
          backgroundColor: theme === 'dark' ? "rgb(221, 204, 225)" : "rgb(221, 204, 225)",
        },
      });

      const clamped = 370;
      setStatementDateRange((prev) => {
        const to = prev?.to || dayjs().format("YYYY-MM-DD");
        const newFrom = dayjs(to).subtract(clamped, "day").format("YYYY-MM-DD");
        return {
          ...prev,
          days: clamped,
          from: newFrom,
        };
      });
      return;
    }

    setStatementDateRange((prev) => {
      const to = prev?.to || dayjs().format("YYYY-MM-DD");
      const newFrom = dayjs(to).subtract(newDiff, "day").format("YYYY-MM-DD");

      return {
        ...prev,
        days: newDiff,
        from: newFrom,
      };
    });
    // setStatementDateRange((prev) => ({ ...prev, days: newDiff }));

    // // push start date to be exactly `newDiff` days before endDate
    // const newStart = dayjs(statementDateRange.to).subtract(newDiff, "day").format("YYYY-MM-DD");
    // setStatementDateRange((prev) => ({ ...prev, from: newStart }));
  };

  const getInvestorTransactions = async () => {
    setLoading(true);
    try {
        const statement = await getUnitholderStatement({
          email: user.data.email,
          folio_no: user.data.folio_no,
          from: updatedStatement.from,
          to: updatedStatement.to,
          fundCode: updatedStatement.fund
        });

        if (!statement.error) {
          const updatedTxns =
            statement.data.balance_unit?.flatMap((item) => {
              const { forPeriodTxns, ...itemData } = item;
              const sortedTxns = [...forPeriodTxns || []].sort((a, b) => parseDate(a).diff(parseDate(b)));
              let running = item.openingUnits;

              const withCurrentBalance = sortedTxns.map((txn) => {
                let operand = 0;
                if (txn.txnr_type === "saleofunit" || txn.txnr_type === "conversionin") {
                  operand = txn.units;
                } else if (txn.txnr_type === "redemptionofunits" || txn.txnr_type === "conversionout") {
                  operand = -txn.units;
                } else if (txn.txnr_type === "transferofunits") {
                  operand = txn.folio_no === user?.data.folio_no ? -txn.units : txn.units;
                }

                running += operand;
                return {
                  txnData: { ...txn },
                  ...itemData,
                  current_balance: running,
                };
              });

              if (running !== item.closingUnits) {
                // console.warn(`Balance mismatch for item ${item.id}: computed ${running}, expected ${item.closingUnits}`);
              }

              return withCurrentBalance;
            }) ?? [];

          setTransactions(updatedTxns);
        }
    } catch (e) {
      //
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const resetStatementModal = () => {
        // const currentFund = derivedFundsRef.current && derivedFundsRef.current?.length !== 0 ? derivedFundsRef.current?.[0] : null;

      //   if (currentFund) {
      //     setStatementDateRange(() => ({
      //       from: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
      //       to: dayjs().format("YYYY-MM-DD"),
      //       days: 30,
      //       category: { name: "All" },
      //       fund: currentFund,
      //       search: "",
      //       date: "month",
      //     }));
      //     return;
      //   }

      // setStatementDateRange(() => ({
      //   from: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
      //   to: dayjs().format("YYYY-MM-DD"),
      //   days: 30,
      //   category: { name: "All" },
      //   fund: null,
      //   search: "",
      //   date: "month",
      // }));
    };

    if (statementModalRef.current) {
      statementModalInstanceRef.current = new Modal(statementModalRef.current, { backdrop: true, keyboard: true });

      statementModalRef.current.addEventListener("hidden.bs.modal", resetStatementModal);
    }

    return () => {
      statementModalRef.current?.removeEventListener("hidden.bs.modal", resetStatementModal);

      statementModalInstanceRef.current?.dispose();
    };
  }, []);

  useEffect(() => {
    getInvestorTransactions();
  }, [updatedStatement]);

  useEffect(() => {
    if (portfolioFunds && portfolioFunds.length !== 0 && purchaseSearchModal) {
      const currentFund = portfolioFunds.find((item) => item.fund_code === purchaseSearchSymbol);

      if (currentFund) {
        setActiveSubMenu('portfolio');
      }
    }
  }, [portfolioFunds, purchaseSearchModal]);

  return (
    <>
      <div className='col-12 p-0 d-flex flex-column pt-2'>
        <button
          className={`mx-auto text-center my-0 date-range-btn${theme === "dark" ? "-dark" : ""} mt-0 d-flex align-items-center flex-nowrap overflow-auto`}
          type='button'
          onClick={() => {
            if (statementModalRef.current) {
              statementModalInstanceRef.current.toggle();
            }
          }}
          style={{
            minWidth: "390px",
            maxWidth: "100%",
            backgroundColor: theme === "dark" ? "rgb(27 33 43)" : "rgb(210 218 235)",
            color: theme === "dark" ? "rgb(156, 156, 156)" : "rgb(40 51 69)",
            border: "1px solid",
            borderColor: theme === "dark" ? "rgb(59, 70, 102)" : "rgb(124 139 183)",
            borderRadius: "999px",
            lineHeight: "1.1",
            padding: "12px 22px",
            fontSize: "15px",
            transition: "color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out",
            scrollbarWidth: "none",
          }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width={22}
            height={22}
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth={1.25}
            strokeLinecap='round'
            strokeLinejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-calendar'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z' />
            <path d='M16 3v4' />
            <path d='M8 3v4' />
            <path d='M4 11h16' />
            <path d='M11 15h1' />
            <path d='M12 15v3' />
          </svg>
          <span
            className=''
            style={{
              fontSize: "17px",
              marginInline: "12px",
              color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(141 42 147)",
            }}
          >
            {dayjs(updatedStatement.from).format("DD MMM YYYY")}
          </span>
          To
          <span
            className='mx-3'
            style={{
              fontSize: "17px",
              color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(141 42 147)",
            }}
          >
            {dayjs(updatedStatement.to).format("DD MMM YYYY")}
          </span>
          <BankIcon width={22} height={22} />
          <span
            className=''
            style={{
              fontSize: "17px",
              marginInlineStart: "12px",
              letterSpacing: ".02em",
              color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(141 42 147)",
              fontWeight: "600",
            }}
          >
            {updatedStatement.fund ? updatedStatement.fund : t("requests.tabs.all")}
          </span>
        </button>

        {loading ? (
          <div
            className='position-relative w-100'
            style={{
              height: "calc(100dvh - 340px)",
            }}
          >
            <div
              className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
              style={{
                color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
              }}
            >
              <div
                className=''
                role='status'
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "300",
                  letterSpacing: ".02em",
                }}
              >
                {t("app.loading.stmnt")}
              </div>
              <div
                className='spinner-border'
                style={{
                  fontSize: "11px",
                  height: "24px",
                  width: "24px",
                  color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                }}
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          </div>
        ) : (
          <Transactions data={transactions} />
        )}
      </div>

      {/* -- STATEMENT DATE RANGE - MODAL -- */}
      <div className='modal fade' id='statementModal' tabIndex='-1' aria-labelledby='statementModal' aria-hidden='true' ref={statementModalRef}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "600px",
            margin: screenSize >= 616 ? "auto" : ".5rem",
          }}
        >
          <div
            className='modal-content'
            style={{
              backgroundColor: theme === "light" ? "rgb(250, 250, 250)" : "rgb(50 ,50 ,50)",
              color: theme === "light" ? "rgb(0,0,0)" : "rgb(232, 232, 232)",
            }}
          >
            <div className='modal-header align-items-center justify-content-between border-0'>
              <div style={{ height: "32px", width: "32px", marginLeft: ".15rem" }}></div>

              <h1
                className='modal-title'
                id='goalModal'
                style={{
                  fontWeight: "400",
                  fontSize: "1.5rem",
                  letterSpacing: ".025em",
                }}
              >
                {t("modals.statement.title")}
              </h1>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  ["--bs-btn-close-bg"]: `url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27%23${theme === "light" ? "000" : "fff"}%27%3e%3cpath d=%27M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z%27/%3e%3c/svg%3e")`,
                }}
                onClick={() => {
                  if (statementModalRef.current) {
                    statementModalInstanceRef.current.toggle();
                  }
                }}
              ></button>
            </div>

            {/* <hr className='m-0 mx-3' /> */}

            <div className='modal-body pt-2'>
              <div className='mt-2'>
                <FundSelector
                  ref={statementModalRef}
                  title={t("modals.statement.form.fund")}
                  data={statementDateRange}
                  updateData={setStatementDateRange}
                  filterItems={fundCategories}
                  items={searchSelectFunds}
                  onItemSelect={(fundItem) => {
                    // setStatementDateRange((prev) => ({ ...prev, fund: fundItem }));
                    setStatementDateRange((prev) => {
                      if (prev.fund && prev.fund?.symbol_code === fundItem?.symbol_code) {
                        return {
                          ...prev,
                          fund: null
                        }
                      }

                      return {
                        ...prev,
                        fund: fundItem
                      }
                    });
                  }}
                />
              </div>
              <div className='mt-4'>
                <label
                  htmlFor='days'
                  className='form-label ps-1 mb-2'
                  style={{
                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  {t("modals.statement.form.duration")}
                </label>
                <div className='d-flex align-items-center justify-content-start gap-2 w-100 overflow-auto'>
                  <button
                    className={`btn${statementDateRange.date === "week" ? ' active' : ''}`}
                    type='button'
                    onClick={() => handleRangePreset("week")}
                    style={{
                      paddingInline: "14px",
                      paddingBlock: "5.5px 5px",
                      borderRadius: "999px",
                      textWrap: "nowrap",
                      fontSize: "14px",
                      letterSpacing: ".04em",
                      minWidth: "82px",
                      // backgroundColor: statementDateRange.date === "week" ? "rgba(203, 110, 209, .6)" : "",
                      ...tabButton(theme),
                      // minWidth: '120px',
                      // maxWidth: '200px'
                    }}
                  >
                    {t("modals.statement.form.week")}
                  </button>
                  <button
                    className={`btn${statementDateRange.date === "month" ? ' active' : ''}`}
                    type='button'
                    onClick={() => handleRangePreset("month")}
                    style={{
                      paddingInline: "14px",
                      paddingBlock: "5.5px 5px",
                      borderRadius: "999px",
                      textWrap: "nowrap",
                      fontSize: "14px",
                      letterSpacing: ".04em",
                      minWidth: "82px",
                      // backgroundColor: statementDateRange.date === "month" ? "rgba(203, 110, 209, .6)" : "",
                      ...tabButton(theme),
                      // minWidth: '120px',
                      // maxWidth: '200px'
                    }}
                  >
                    {t("modals.statement.form.month")}
                  </button>
                  <button
                    className={`btn${statementDateRange.date === "quarter" ? ' active' : ''}`}
                    type='button'
                    onClick={() => handleRangePreset("quarter")}
                    style={{
                      paddingInline: "14px",
                      paddingBlock: "5.5px 5px",
                      borderRadius: "999px",
                      textWrap: "nowrap",
                      fontSize: "14px",
                      letterSpacing: ".04em",
                      minWidth: "82px",
                      // backgroundColor: statementDateRange.date === "quarter" ? "rgba(203, 110, 209, .6)" : "",
                      ...tabButton(theme),
                      // minWidth: '120px',
                      // maxWidth: '200px'
                    }}
                  >
                    {t("modals.statement.form.quarter")}
                  </button>
                  <button
                    className={`btn${statementDateRange.date === "half" ? ' active' : ''}`}
                    type='button'
                    onClick={() => handleRangePreset("half")}
                    style={{
                      paddingInline: "14px",
                      paddingBlock: "5.5px 5px",
                      borderRadius: "999px",
                      textWrap: "nowrap",
                      fontSize: "14px",
                      letterSpacing: ".04em",
                      minWidth: "92px",
                      // backgroundColor: statementDateRange.date === "half" ? "rgba(203, 110, 209, .6)" : "",
                      ...tabButton(theme),
                      // minWidth: '120px',
                      // maxWidth: '200px'
                    }}
                  >
                    {t("modals.statement.form.half")}
                  </button>
                  <button
                    className={`btn${statementDateRange.date === "year" ? ' active' : ''}`}
                    type='button'
                    onClick={() => handleRangePreset("year")}
                    style={{
                      paddingInline: "14px",
                      paddingBlock: "5.5px 5px",
                      borderRadius: "999px",
                      textWrap: "nowrap",
                      fontSize: "14px",
                      letterSpacing: ".04em",
                      minWidth: "82px",
                      // backgroundColor: statementDateRange.date === "year" ? "rgba(203, 110, 209, .6)" : "",
                      ...tabButton(theme),
                      // minWidth: '120px',
                      // maxWidth: '200px'
                    }}
                  >
                    {t("modals.statement.form.year")}
                  </button>
                  <button
                    className={`btn${statementDateRange.date === "date" ? ' active' : ''}`}
                    type='button'
                    onClick={() => setStatementDateRange((prev) => ({ ...prev, date: "date" }))}
                    style={{
                      paddingInline: "14px",
                      paddingBlock: "5.5px 5px",
                      borderRadius: "999px",
                      textWrap: "nowrap",
                      fontSize: "14px",
                      minWidth: "92px",
                      letterSpacing: ".04em",
                      // backgroundColor: statementDateRange.date === "date" ? "rgba(203, 110, 209, .6)" : "",
                      ...tabButton(theme),
                      // minWidth: '120px',
                      // maxWidth: '200px'
                    }}
                  >
                    {t("modals.statement.form.duration")}
                  </button>
                </div>
              </div>
              {statementDateRange.date === "date" ? (
                <>
                  <div className='row w-100 mx-auto mt-4 row-gap-4'>
                    <div className='col-12 col-sm-auto p-0 pe-sm-3'>
                      <label
                        htmlFor='date-from'
                        className='form-label ps-1 mb-1'
                        style={{
                          color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                        }}
                      >
                        {t("modals.statement.form.days")}
                      </label>
                      <input
                        type='text'
                        name='number'
                        value={statementDateRange.days}
                        onChange={handleDaysChange}
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                        id='days'
                        style={{
                          ...themeBasedGoalFormControl(theme),
                          width: "auto",
                          maxWidth: "110px",
                          // backgroundColor: 'rgb(238, 242, 246)',
                        }}
                      />
                    </div>
                    <div className='col-12 col-sm p-0 px-sm-2'>
                      <label
                        htmlFor='date-from'
                        className='form-label ps-1 mb-1'
                        style={{
                          color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                        }}
                      >
                        {t("modals.statement.form.from")}
                      </label>
                      <input
                        type='date'
                        name='date-from'
                        value={statementDateRange.from}
                        onChange={handleStatementDateChange}
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                        id='date-from'
                        style={{
                          ...themeBasedGoalFormControl(theme),
                          // backgroundColor: 'rgb(238, 242, 246)',
                        }}
                      />
                    </div>
                    <div className='col-12 col-sm p-0 ps-sm-3'>
                      <label
                        htmlFor='date-to'
                        className='form-label ps-1 mb-1'
                        style={{
                          color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                        }}
                      >
                        {t("modals.statement.form.to")}
                      </label>
                      <input
                        type='date'
                        name='date-to'
                        value={statementDateRange.to}
                        onChange={handleStatementDateChange}
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                        id='date-to'
                        style={{
                          ...themeBasedGoalFormControl(theme),
                          // backgroundColor: 'rgb(238, 242, 246)',
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                ""
              )}
            </div>

            <div className='modal-footer border-0 justify-content-between mt-2'>
              <button
                type='button'
                className='btn border-0'
                onClick={() => {
                  if (statementModalRef.current) {
                    statementModalInstanceRef.current.toggle();
                  }
                }}
                style={{
                  // ["--bs-btn-bg"]: 'rgb(79 56 80)',
                  ...customModalCancelButton,
                  // backgroundColor: 'rgb(100 83 100)',
                  minWidth: "90px",
                }}
              >
                {t("modals.common.cancel")}
              </button>
              <button
                type='button'
                className='btn border-0 ms-2'
                disabled={loading.addPortfolioModal}
                onClick={() => {
                  handleFilterStatement();
                  if (statementModalRef.current) {
                    statementModalInstanceRef.current.toggle();
                  }
                }}
                style={{
                  ...customPortfolioModalButton,
                  minWidth: "90px",
                }}
              >
                {t("modals.common.confirm")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
